import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPinned, Phone, IndianRupee, ClipboardList, Play, Pause, RotateCcw, History, AlertTriangle, WifiOff, LogIn, Sunrise, MoonStar } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner, EmptyState } from "../../components/common";
import { FetchButton, FetchPrompt } from "../../components/FetchBar";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Slider } from "../../components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { MultiSelect } from "../../components/ui/multi-select";
import { StaffAvatar, StaffLabel, avatarMarkerHtml, useAvatars, resolveStaff, staffLabelText } from "../../components/StaffAvatar";
import { api, errMsg, fmtINR, fmtTimeIST } from "../../lib/api";

const DEFAULT_CENTER = [21.2095, 81.379]; // Bhilai


// Idle Alerts (Phase 68)
const fmtDur = (min) => {
  const m = Math.max(parseInt(min, 10) || 0, 0);
  const h = Math.floor(m / 60);
  return h ? `${h}h ${m % 60}m` : `${m}m`;
};
const isFlagged = (a) => a.idle && (a.idle.status === "idle" || a.idle.status === "no_signal");
// Phase 144: outside the tenant's duty hours (start–end IST) no idle status is computed and no alert is sent
const isAfterHours = (a) => a.idle?.status === "off_duty" && a.idle?.reason === "outside_duty_hours";

// Phase 87: presence = "signed in today (IST)"; explicit logout flips it off until the next activity
const PRESENCE = {
  logged_in: { label: "Logged in", color: "#10b981", chip: "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100",
    active: "bg-emerald-600 text-white border-emerald-600", dot: "bg-emerald-500" },
  not_logged_in: { label: "Not logged in", color: "#ef4444", chip: "bg-red-50 text-red-700 border-red-200 hover:bg-red-100",
    active: "bg-red-600 text-white border-red-600", dot: "bg-red-500" },
};
const presenceOf = (a) => (a.presence?.status === "logged_in" ? "logged_in" : "not_logged_in");
const presenceTitle = (a) => {
  const p = a.presence || {};
  if (p.status === "logged_in") return `Logged in today • last active ${timeOf(p.last_activity_at)}`;
  if (p.reason === "logged_out") return `Logged out at ${timeOf(p.logged_out_at)}`;
  return "Not logged in today";
};

// Phase 88: first sign-in of the day (IST) + late-start flag (after the tenant's duty start time)
const SignInTime = ({ agent, compact = false }) => {
  const p = agent.presence || {};
  if (!p.first_seen_at) {
    return (
      <span className="inline-flex items-center gap-1 text-muted-foreground" title="No sign-in recorded today" data-testid={`signin-time-${agent.id}`}>
        <LogIn className="h-3 w-3" /> {compact ? "—" : "No sign-in today"}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 flex-wrap" data-testid={`signin-time-${agent.id}`} data-late={p.late ? "true" : "false"}
      title={`First sign-in today ${fmtTimeIST(p.first_seen_at)} IST${p.duty_start ? ` • duty starts ${p.duty_start}` : ""}`}>
      <LogIn className="h-3 w-3 text-muted-foreground" />
      <span className="tabular-nums">{compact ? "" : "Signed in "}{fmtTimeIST(p.first_seen_at)}</span>
      {p.late && (
        <span className="inline-flex items-center gap-0.5 rounded-full border border-amber-300 bg-amber-100 px-1.5 py-px text-[10px] font-semibold text-amber-800"
          data-testid={`late-start-badge-${agent.id}`}>
          <Sunrise className="h-2.5 w-2.5" /> Late start
        </span>
      )}
    </span>
  );
};

const PresenceDot = ({ agent, className = "" }) => {
  const s = presenceOf(agent);
  return (
    <span className={`inline-block h-2.5 w-2.5 rounded-full ring-2 ring-background ${PRESENCE[s].dot} ${className}`}
      title={presenceTitle(agent)} aria-label={PRESENCE[s].label} data-testid={`presence-dot-${agent.id}`} data-presence={s} />
  );
};

// Phase 86: pins show the agent's photo (or initials); the ring colour still encodes state
// (amber = idle, red = no signal, brown = selected, teal = supervisor, blue = collector).
// Phase 87: a small green/red dot on the pin shows whether the agent signed in today.
const agentIcon = (agent, selected, photoUrl) => {
  const idleStatus = agent.idle?.status;
  const ring = idleStatus === "idle" ? "#f59e0b" : idleStatus === "no_signal" ? "#ef4444"
    : selected ? "#b45309" : agent.role === "supervisor" ? "#0f766e" : "#0b5686";
  const size = selected ? 44 : 38;
  const presence = presenceOf(agent);
  return L.divIcon({
    className: "",
    html: avatarMarkerHtml({ id: agent.id, name: agent.name, photoUrl, ringColor: ring, size, live: idleStatus !== "no_signal",
      dot: PRESENCE[presence].color, dotTitle: presenceTitle(agent) }),
    iconSize: [size, size + 8],
    iconAnchor: [size / 2, size + 8],
    popupAnchor: [0, -(size + 4)],
  });
};

function FitBounds({ points }) {
  const map = useMap();
  const key = JSON.stringify(points);
  useEffect(() => {
    if (points.length === 1) map.setView(points[0], 15);
    else if (points.length > 1) map.fitBounds(L.latLngBounds(points).pad(0.25), { maxZoom: 15 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, map]);
  return null;
}

function timeOf(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function TrackingMap() {
  const { code } = useParams();
  const avatars = useAvatars(code); // Phase 86: photos for map pins (re-renders when the directory loads)
  const [agents, setAgents] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all"); // Phase 87: all | logged_in | not_logged_in
  const [zoneFilter, setZoneFilter] = useState([]);        // Phase 113 / 176: [] = all | [<zone id>, …, __unmapped__] (multi-select)
  const [zones, setZones] = useState([]);
  const [trail, setTrail] = useState([]);
  const [trailDate, setTrailDate] = useState(new Date().toISOString().slice(0, 10));
  const [appliedTrailDate, setAppliedTrailDate] = useState(trailDate); // Phase 133: the date the trail was fetched for
  const [trailVersion, setTrailVersion] = useState(0);
  const [loadingTrail, setLoadingTrail] = useState(false);
  // Phase 133: nothing loads until the user clicks Fetch; the 30s live refresh starts only after that
  const [fetched, setFetched] = useState(false);
  const [loadingAgents, setLoadingAgents] = useState(false);
  // Route Playback (Phase 64): scrub / replay the agent's day hour by hour
  const [playT, setPlayT] = useState(null);       // current playback timestamp (ms); null = full trail
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState("1");        // route-minutes per real second multiplier

  const load = useCallback(() => {
    setLoadingAgents(true);
    api.get(`/t/${code}/tracking/agents`).then((r) => setAgents(r.data)).catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoadingAgents(false));
  }, [code]);

  useEffect(() => {
    if (!fetched) return undefined;
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load, fetched]);

  const fetchAll = () => {
    setFetched(true);
    if (fetched) load();
    setAppliedTrailDate(trailDate);
    setTrailVersion((v) => v + 1);
  };

  // Phase 113: zones for the Zone filter (supervisors only receive their own mapped zones)
  useEffect(() => {
    api.get(`/t/${code}/masters/zones`, { params: { include_inactive: false } })
      .then((r) => setZones(Array.isArray(r.data) ? r.data : r.data?.items || []))
      .catch(() => setZones([]));
  }, [code]);

  const selected = (agents || []).find((a) => a.id === selectedId) || null;

  const loadTrail = useCallback(async (agentId, date) => {
    setLoadingTrail(true);
    try {
      const r = await api.get(`/t/${code}/tracking/agents/${agentId}/trail`, { params: { date } });
      setTrail(r.data);
      if (r.data.length === 0) toast.info("No GPS points logged for this agent on the selected date");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoadingTrail(false);
    }
  }, [code]);

  // the trail loads when an agent is picked (that click is the request) or when "Fetch trail" is clicked for a date
  useEffect(() => {
    if (selectedId && fetched) loadTrail(selectedId, appliedTrailDate);
    else setTrail([]);
    setPlayT(null);
    setPlaying(false);
  }, [selectedId, appliedTrailDate, trailVersion, fetched, loadTrail]);
  const fetchTrail = () => { setAppliedTrailDate(trailDate); setTrailVersion((v) => v + 1); };

  // ---- Route Playback derived state ----
  const trailTimes = useMemo(() => trail.map((t) => new Date(t.logged_at).getTime()), [trail]);
  const tMin = trailTimes.length ? trailTimes[0] : null;
  const tMax = trailTimes.length ? trailTimes[trailTimes.length - 1] : null;
  const playbackActive = playT != null && trail.length > 1;
  const playedTrail = useMemo(
    () => (playbackActive ? trail.filter((_, i) => trailTimes[i] <= playT) : trail),
    [playbackActive, trail, trailTimes, playT]);
  const currentPoint = playbackActive && playedTrail.length ? playedTrail[playedTrail.length - 1] : null;
  const hourMarks = useMemo(() => {
    if (tMin == null || tMax == null || tMax <= tMin) return [];
    const first = new Date(tMin);
    first.setMinutes(0, 0, 0);
    const out = [];
    for (let h = first.getTime(); h <= tMax; h += 3600000) out.push(h);
    return out;
  }, [tMin, tMax]);

  // ticker: at 1x one real second replays 15 route-minutes (a full 8h day in ~32s)
  useEffect(() => {
    if (!playing || tMin == null || tMax == null) return;
    const id = setInterval(() => {
      setPlayT((prev) => {
        const base = prev == null || prev >= tMax ? tMin : prev;
        const next = base + Number(speed) * 90 * 1000;
        if (next >= tMax) { setPlaying(false); return tMax; }
        return next;
      });
    }, 100);
    return () => clearInterval(id);
  }, [playing, speed, tMin, tMax]);

  const hasSupervisors = (agents || []).some((a) => a.role === "supervisor");
  const roleOf = (a) => a.role || "tax_collector";
  // Phase 113: the Zone filter is applied first, so every count below (role chips, Logged in /
  // Not logged in) describes only the staff belonging to the selected zone.
  const UNMAPPED = "__unmapped__";
  // Phase 176: multi-select — an agent is shown when it belongs to ANY selected zone (or is unmapped when that option is ticked)
  const zoneActive = zoneFilter.length > 0;
  const inZone = (a) => !zoneActive
    || (zoneFilter.includes(UNMAPPED) && !(a.zone_ids || []).length)
    || (a.zone_ids || []).some((zid) => zoneFilter.includes(zid));
  const zoneAgents = (agents || []).filter(inZone);
  const unmappedCount = (agents || []).filter((a) => !(a.zone_ids || []).length).length;
  const zoneOptions = useMemo(() => {
    const seen = new Set();
    const opts = [];
    zones.forEach((z) => { if (!seen.has(z.id)) { seen.add(z.id); opts.push({ id: z.id, name: z.zone_name || z.id }); } });
    // zones referenced by agents but missing from the (scoped) master list still get an option
    (agents || []).forEach((a) => (a.zone_ids || []).forEach((zid, i) => {
      if (!seen.has(zid)) { seen.add(zid); opts.push({ id: zid, name: a.zones?.[i] || zid }); }
    }));
    return opts;
  }, [zones, agents]);
  const zoneCountOf = (zid) => (agents || []).filter((a) => (a.zone_ids || []).includes(zid)).length;
  const zoneNameOf = (zid) => (zid === UNMAPPED ? "Not mapped to a zone" : zoneOptions.find((z) => z.id === zid)?.name || zid);
  const selectedZoneName = !zoneActive ? null : zoneFilter.map(zoneNameOf).join(", ");
  const changeZone = (v) => { setZoneFilter(Array.isArray(v) ? v : []); setSelectedId(null); };
  const zoneSelectOptions = useMemo(() => [
    ...zoneOptions.map((z) => ({ value: z.id, label: z.name, hint: `${zoneCountOf(z.id)} staff` })),
    ...(unmappedCount > 0 ? [{ value: UNMAPPED, label: "Not mapped to a zone", hint: `${unmappedCount} staff` }] : []),
  ], [zoneOptions, agents, unmappedCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const roleAgents = zoneAgents.filter((a) => roleFilter === "all" || roleOf(a) === roleFilter);
  // Phase 87: role chips count every agent (in the selected zone); status chips count within the selected role
  const roleCounts = {
    all: zoneAgents.length,
    tax_collector: zoneAgents.filter((a) => roleOf(a) === "tax_collector").length,
    supervisor: zoneAgents.filter((a) => roleOf(a) === "supervisor").length,
  };
  const statusCounts = {
    logged_in: roleAgents.filter((a) => presenceOf(a) === "logged_in").length,
    not_logged_in: roleAgents.filter((a) => presenceOf(a) === "not_logged_in").length,
  };
  const visibleAgents = roleAgents.filter((a) => statusFilter === "all" || presenceOf(a) === statusFilter);
  const toggleStatus = (s) => { setStatusFilter((cur) => (cur === s ? "all" : s)); setSelectedId(null); };
  const idleThreshold = (agents || []).find((a) => a.idle)?.idle?.threshold || 60;
  // Phase 144: tenant duty hours (same for every agent) + whether we are currently outside them
  const dutyInfo = (agents || []).find((a) => a.presence?.duty_hours)?.presence || {};
  const afterHoursNow = (agents || []).length > 0 && (agents || []).every(isAfterHours);
  const located = visibleAgents.filter((a) => a.last_location);
  const fitPoints = useMemo(() => {
    if (trail.length > 0) return trail.map((t) => [t.latitude, t.longitude]);
    if (selected?.last_location) return [[selected.last_location.latitude, selected.last_location.longitude]];
    return located.map((a) => [a.last_location.latitude, a.last_location.longitude]);
  }, [trail, selected, located]);

  if (!fetched) {
    return (
      <div>
        <PageHeader title="Agent Tracking" subtitle="Live locations & day trails — tax collectors and supervisors">
          <FetchButton onClick={fetchAll} fetched={false} testId="tracking-fetch-button" />
        </PageHeader>
        <Card className="shadow-sm"><CardContent className="p-0">
          <FetchPrompt onFetch={fetchAll} testId="tracking-fetch-prompt" title="Click Fetch to load live agent locations"
            subtitle="Staff positions and the map load only when you ask for them. After the first Fetch the map keeps refreshing live every 30 seconds; use the Zone / role / status filters to narrow the list." />
        </CardContent></Card>
      </div>
    );
  }
  if (!agents) return <Spinner label="Loading agents..." />;

  return (
    <div>
      <PageHeader title="Agent Tracking"
        subtitle={selectedZoneName ? `Live locations & day trails — ${selectedZoneName}` : "Live locations & day trails — tax collectors and supervisors"}>
        {/* Phase 113 / 176: Zone filter (multi-select) — list, map and all counts follow the selected zones */}
        <div className="flex items-center gap-1.5" data-testid="tracking-zone-filter">
          <MapPinned className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
          <MultiSelect options={zoneSelectOptions} value={zoneFilter} onChange={changeZone}
            allLabel={`All zones (${(agents || []).length})`} noun="zone" placeholder="Search zones…"
            className="h-9 w-[200px] sm:w-[240px] text-sm" testId="tracking-zone-select" />
          {zoneActive && (
            <Button variant="ghost" size="sm" className="h-9 px-2 text-xs" onClick={() => changeZone([])} data-testid="tracking-zone-clear">
              Clear
            </Button>
          )}
        </div>
        <FetchButton onClick={fetchAll} loading={loadingAgents} fetched size="sm" className="h-9 min-w-0" testId="tracking-fetch-button" />
      </PageHeader>

      <div className="grid gap-4 lg:grid-cols-[340px_1fr]">
        <div className="space-y-3 lg:max-h-[640px] lg:overflow-y-auto pr-1" data-testid="tracking-agent-list">
          {/* Phase 114: idle alerts are shown inline on each staff card (see below) — no separate panel */}
          <div className="space-y-2" data-testid="tracking-filter-bar">
            {hasSupervisors && (
              <div className="flex flex-wrap gap-1.5" data-testid="tracking-role-filter">
                {[["all", "All"], ["tax_collector", "Collectors"], ["supervisor", "Supervisors"]].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => { setRoleFilter(v); setSelectedId(null); }}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors inline-flex items-center gap-1.5 ${roleFilter === v
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background text-muted-foreground hover:bg-muted"}`}
                    aria-pressed={roleFilter === v} data-testid={`tracking-filter-${v}`}>
                    {l}
                    <span className={`tabular-nums text-[10px] font-semibold rounded-full px-1.5 py-px ${roleFilter === v ? "bg-primary-foreground/20" : "bg-muted"}`}
                      data-testid={`tracking-filter-${v}-count`}>{roleCounts[v]}</span>
                  </button>
                ))}
              </div>
            )}
            {/* Phase 87: presence chips — green = signed in today (IST), red = not signed in / logged out */}
            <div className="flex flex-wrap items-center gap-1.5" data-testid="tracking-status-filter">
              {!hasSupervisors && (
                <span className="text-xs px-2.5 py-1 rounded-full border bg-primary text-primary-foreground border-primary inline-flex items-center gap-1.5"
                  data-testid="tracking-filter-all">
                  All <span className="tabular-nums text-[10px] font-semibold rounded-full px-1.5 py-px bg-primary-foreground/20" data-testid="tracking-filter-all-count">{roleCounts.all}</span>
                </span>
              )}
              {["logged_in", "not_logged_in"].map((s) => {
                const on = statusFilter === s;
                return (
                  <button key={s} type="button" onClick={() => toggleStatus(s)} aria-pressed={on}
                    title={on ? "Click again to show everyone" : `Show only ${PRESENCE[s].label.toLowerCase()} agents`}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors inline-flex items-center gap-1.5 ${on ? PRESENCE[s].active : PRESENCE[s].chip}`}
                    data-testid={`tracking-status-${s}`} data-active={on ? "true" : "false"}>
                    <span className={`inline-block h-2 w-2 rounded-full ${on ? "bg-white" : PRESENCE[s].dot}`} aria-hidden="true" />
                    {PRESENCE[s].label}
                    <span className={`tabular-nums text-[10px] font-semibold rounded-full px-1.5 py-px ${on ? "bg-white/25" : "bg-white/70"}`}
                      data-testid={`tracking-status-${s}-count`}>{statusCounts[s]}</span>
                  </button>
                );
              })}
              {statusFilter !== "all" && (
                <button type="button" onClick={() => toggleStatus(statusFilter)}
                  className="text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
                  data-testid="tracking-status-clear">Clear</button>
              )}
            </div>
            {/* Phase 144: duty-hours hint — idle alerts are only computed inside the tenant's duty window */}
            {dutyInfo.duty_end && (
              <p className={`text-[11px] flex items-center gap-1.5 rounded-md border px-2 py-1 ${afterHoursNow ? "bg-slate-50 text-slate-600" : "bg-muted/40 text-muted-foreground"}`}
                data-testid="tracking-duty-hours-hint" data-after-hours={afterHoursNow ? "true" : "false"}>
                <MoonStar className="h-3 w-3 shrink-0" aria-hidden="true" />
                <span>Duty hours <span className="font-medium tabular-nums">{dutyInfo.duty_start}–{dutyInfo.duty_end} IST</span>
                  {afterHoursNow ? " — after hours now, idle alerts paused" : " — idle alerts active"}</span>
              </p>
            )}
          </div>
          {visibleAgents.length === 0 && (
            <EmptyState
              title={zoneActive && zoneAgents.length === 0 ? `No field staff in ${selectedZoneName}`
                : statusFilter !== "all" ? `No ${PRESENCE[statusFilter].label.toLowerCase()} agents` : "No active field staff"}
              subtitle={zoneActive && zoneAgents.length === 0 ? "Pick other zones or clear the zone filter."
                : statusFilter !== "all" ? "Try the other status or clear the filter." : undefined} />
          )}
          {visibleAgents.map((a) => (
            <Card key={a.id}
              className={`shadow-sm cursor-pointer transition-shadow hover:shadow-md ${selectedId === a.id ? "ring-2 ring-primary" : ""}`}
              onClick={() => setSelectedId(selectedId === a.id ? null : a.id)}
              data-testid={`tracking-agent-card-${a.id}`} data-presence={presenceOf(a)}>
              <CardContent className="p-3.5">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="relative shrink-0">
                      <StaffAvatar code={code} userId={a.id} name={a.name} size="md" testId={`tracking-avatar-${a.id}`}
                        className={a.role === "supervisor" ? "ring-2 ring-teal-600" : "ring-2 ring-primary/60"} />
                      <PresenceDot agent={a} className="absolute -right-0.5 -bottom-0.5" />
                    </span>
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">
                        <StaffLabel code={code} userId={a.id} name={a.name} empCode={a.employee_id} />
                        {a.role === "supervisor" && (
                          <span className="ml-1.5 text-[10px] font-semibold text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-1.5 py-0.5 align-middle"
                            data-testid={`tracking-supervisor-badge-${a.id}`}>
                            Supervisor
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1 flex-wrap">
                        <Phone className="h-3 w-3" /> {a.mobile || "-"}
                        <span className="text-muted-foreground/60">•</span>
                        <span className={`font-medium ${presenceOf(a) === "logged_in" ? "text-emerald-700" : "text-red-700"}`}
                          data-testid={`presence-label-${a.id}`}>{presenceOf(a) === "logged_in" ? "Logged in" : "Not logged in"}</span>
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-0.5"><SignInTime agent={a} /></p>
                    </div>
                  </div>
                  {a.idle?.status === "idle" ? (
                    <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 text-[10px]"
                      data-testid={`idle-badge-${a.id}`}>
                      Idle {fmtDur(a.idle.idle_minutes)}
                    </Badge>
                  ) : a.idle?.status === "no_signal" ? (
                    <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-[10px] flex items-center gap-1"
                      data-testid={`idle-badge-${a.id}`}>
                      <WifiOff className="h-2.5 w-2.5" /> No signal
                    </Badge>
                  ) : isAfterHours(a) ? (
                    <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-200 text-[10px] flex items-center gap-1"
                      title={`Outside duty hours (${a.idle.duty_hours || a.presence?.duty_hours || ""}) — idle alerts paused`}
                      data-testid={`after-hours-badge-${a.id}`}>
                      <MoonStar className="h-2.5 w-2.5" /> After hours{a.last_location ? ` · ${timeOf(a.last_location.logged_at)}` : ""}
                    </Badge>
                  ) : a.last_location ? (
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">
                      {timeOf(a.last_location.logged_at)}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-slate-100 text-slate-600 text-[10px]">No location</Badge>
                  )}
                </div>
                {/* Phase 114: idle alert inline on the card (replaces the former top-of-page panel) */}
                {isFlagged(a) && (
                  <div role="status"
                    className={`mt-2.5 flex items-start gap-2 rounded-md border px-2.5 py-2 text-xs leading-snug ${a.idle.status === "idle"
                      ? "border-amber-300 bg-amber-50 text-amber-900"
                      : "border-red-200 bg-red-50 text-red-800"}`}
                    data-testid={`idle-alert-inline-${a.id}`} data-idle-status={a.idle.status}>
                    {a.idle.status === "idle"
                      ? <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px text-amber-600" aria-hidden="true" />
                      : <WifiOff className="h-3.5 w-3.5 shrink-0 mt-px text-red-600" aria-hidden="true" />}
                    <span>
                      {a.idle.status === "idle" ? (
                        <><span className="font-semibold">Idle alert:</span> no movement for {fmtDur(a.idle.idle_minutes)} during duty time
                          {a.idle.idle_since ? ` (since ${timeOf(a.idle.idle_since)})` : ""}. Threshold {fmtDur(a.idle.threshold || idleThreshold)}.</>
                      ) : (
                        <><span className="font-semibold">No GPS signal</span> for {fmtDur(a.idle.idle_minutes)}
                          {a.idle.last_ping_at ? ` — last ping ${timeOf(a.idle.last_ping_at)}` : ""}.</>
                      )}
                    </span>
                  </div>
                )}
                <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                  <div className="bg-muted rounded-md py-1.5">
                    <p className="text-[10px] text-muted-foreground flex items-center justify-center gap-0.5"><IndianRupee className="h-2.5 w-2.5" />Today</p>
                    <p className="text-xs font-semibold tabular-nums">{fmtINR(a.today_amount)}</p>
                  </div>
                  <div className="bg-muted rounded-md py-1.5">
                    <p className="text-[10px] text-muted-foreground">Receipts</p>
                    <p className="text-xs font-semibold tabular-nums">{a.today_count}</p>
                  </div>
                  {a.role !== "supervisor" ? (
                    <div className="bg-muted rounded-md py-1.5">
                      <p className="text-[10px] text-muted-foreground flex items-center justify-center gap-0.5"><ClipboardList className="h-2.5 w-2.5" />Pending</p>
                      <p className="text-xs font-semibold tabular-nums">{a.pending_assignments}</p>
                    </div>
                  ) : (
                    <div className="bg-teal-50 border border-teal-100 rounded-md py-1.5">
                      <p className="text-[10px] text-teal-700">Role</p>
                      <p className="text-xs font-semibold text-teal-800">Field Sup.</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}

          {selected && (
            <Card className="shadow-sm border-primary/40">
              <CardContent className="p-3.5 space-y-2">
                <p className="text-sm font-medium flex items-center gap-1.5"><MapPinned className="h-4 w-4" /> Trail for <StaffLabel code={code} userId={selected.id} name={selected.name} empCode={selected.employee_id} className="ml-1" /></p>
                <div className="flex items-center gap-2">
                  <Input type="date" value={trailDate} onChange={(e) => setTrailDate(e.target.value)} data-testid="trail-date-input" />
                  <FetchButton onClick={fetchTrail} loading={loadingTrail} fetched dirty={trailDate !== appliedTrailDate} size="sm"
                    className="h-9 min-w-0 shrink-0" label="Fetch trail" testId="trail-fetch-button" />
                </div>
                <p className="text-xs text-muted-foreground" data-testid="trail-summary">
                  {loadingTrail ? "Loading trail..." : `${trail.length} GPS point${trail.length === 1 ? "" : "s"} on ${appliedTrailDate}`}
                  {trailDate !== appliedTrailDate && !loadingTrail && <span className="text-[hsl(var(--warning))] font-medium"> · date changed — click Fetch trail</span>}
                </p>

                {trail.length > 1 && (
                  <div className="space-y-2 border-t pt-2.5" data-testid="route-playback-panel">
                    <p className="text-xs font-semibold flex items-center gap-1.5">
                      <History className="h-3.5 w-3.5" /> Route Playback
                    </p>
                    <div className="flex items-center gap-1.5">
                      <Button size="sm" className="h-8"
                        onClick={() => { if (playT == null || playT >= tMax) setPlayT(tMin); setPlaying(!playing); }}
                        data-testid="playback-play-button">
                        {playing ? <><Pause className="h-3.5 w-3.5 mr-1" /> Pause</> : <><Play className="h-3.5 w-3.5 mr-1" /> Play</>}
                      </Button>
                      <Select value={speed} onValueChange={setSpeed}>
                        <SelectTrigger className="h-8 w-[70px]" data-testid="playback-speed-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="z-[1100]">
                          <SelectItem value="1">1x</SelectItem>
                          <SelectItem value="2">2x</SelectItem>
                          <SelectItem value="4">4x</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button size="sm" variant="ghost" className="h-8 px-2" title="Show full trail"
                        onClick={() => { setPlaying(false); setPlayT(null); }} data-testid="playback-reset-button">
                        <RotateCcw className="h-3.5 w-3.5" />
                      </Button>
                      <Badge variant="outline" className="text-[10px] tabular-nums ml-auto" data-testid="playback-time">
                        {playT != null ? timeOf(new Date(playT).toISOString()) : "Full day"}
                      </Badge>
                    </div>
                    <Slider value={[playT ?? tMax]} min={tMin} max={tMax} step={60000}
                      onValueChange={([v]) => setPlayT(v)} data-testid="playback-slider" />
                    <div className="flex justify-between text-[10px] text-muted-foreground tabular-nums">
                      <span>{timeOf(new Date(tMin).toISOString())}</span>
                      <span data-testid="playback-progress">
                        {playbackActive ? `${playedTrail.length}/${trail.length} points` : `${trail.length} points`}
                      </span>
                      <span>{timeOf(new Date(tMax).toISOString())}</span>
                    </div>
                    {hourMarks.length > 1 && (
                      <div className="flex flex-wrap gap-1" data-testid="playback-hour-chips">
                        {hourMarks.map((h) => (
                          <button key={h} type="button"
                            onClick={() => { setPlaying(false); setPlayT(Math.min(Math.max(h, tMin), tMax)); }}
                            className={`text-[10px] px-1.5 py-0.5 rounded border transition-colors ${playT != null && playT >= h && playT < h + 3600000
                              ? "bg-primary text-primary-foreground border-primary"
                              : "bg-background text-muted-foreground hover:bg-muted"}`}
                            data-testid={`playback-hour-${new Date(h).getHours()}`}>
                            {new Date(h).toLocaleTimeString("en-IN", { hour: "numeric" })}
                          </button>
                        ))}
                      </div>
                    )}
                    <p className="text-[10px] text-muted-foreground">
                      Play replays the day; tap an hour to jump; drag the slider to scrub.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* `isolate z-0` keeps Leaflet's internal z-indexes (panes 400, controls 1000) inside this card, so
            page-level dropdowns/popovers (z-50) — e.g. the Zone filter — are never painted over by the map. */}
        <Card className="shadow-sm overflow-hidden relative isolate z-0">
          <div className="h-[560px] lg:h-[640px]" data-testid="tracking-map">
            <MapContainer center={DEFAULT_CENTER} zoom={12} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <FitBounds points={fitPoints} />
              {located.map((a) => (
                <Marker key={a.id} position={[a.last_location.latitude, a.last_location.longitude]}
                  icon={agentIcon(a, selectedId === a.id, resolveStaff(avatars, a.id, a.name)?.photo_url)}
                  eventHandlers={{ click: () => setSelectedId(a.id) }}>
                  <Popup>
                    <div style={{ fontFamily: "Figtree, sans-serif" }}>
                      <p style={{ fontWeight: 600, margin: 0 }}>{staffLabelText(a.name, a.employee_id ?? resolveStaff(avatars, a.id, a.name)?.employee_id)}{a.role === "supervisor" ? " · Supervisor" : ""}</p>
                      <p style={{ margin: "2px 0", fontSize: 12, fontWeight: 600, color: presenceOf(a) === "logged_in" ? "#047857" : "#dc2626" }}
                        data-testid={`popup-presence-${a.id}`}>
                        {presenceTitle(a)}
                      </p>
                      <p style={{ margin: "2px 0", fontSize: 12, color: a.presence?.late ? "#b45309" : "#334155" }} data-testid={`popup-signin-${a.id}`}>
                        First sign-in: {a.presence?.first_seen_at ? `${fmtTimeIST(a.presence.first_seen_at)} IST${a.presence.late ? " • Late start" : ""}` : "none today"}
                      </p>
                      {isFlagged(a) && (
                        <p style={{ margin: "2px 0", fontSize: 12, fontWeight: 600, color: a.idle.status === "idle" ? "#b45309" : "#dc2626" }}>
                          {a.idle.status === "idle" ? `Idle for ${fmtDur(a.idle.idle_minutes)}` : `No GPS signal for ${fmtDur(a.idle.idle_minutes)}`}
                        </p>
                      )}
                      <p style={{ margin: "2px 0", fontSize: 12 }}>{a.employee_id || ""} • {a.mobile || ""}</p>
                      {(a.zones || []).length > 0 && (
                        <p style={{ margin: "2px 0", fontSize: 12 }} data-testid={`popup-zone-${a.id}`}>Zone: {a.zones.join(", ")}</p>
                      )}
                      <p style={{ margin: "2px 0", fontSize: 12 }}>Today: {fmtINR(a.today_amount)} ({a.today_count} receipts)</p>
                      {a.role !== "supervisor" && (
                        <p style={{ margin: "2px 0", fontSize: 12 }}>Pending assignments: {a.pending_assignments}</p>
                      )}
                      {/* Phase 113: exact GPS coordinates of the last fix */}
                      <p style={{ margin: "4px 0 2px", fontSize: 12, fontFamily: "'Roboto Mono', monospace" }} data-testid={`popup-coords-${a.id}`}>
                        Lat {Number(a.last_location.latitude).toFixed(6)}, Lng {Number(a.last_location.longitude).toFixed(6)}
                        {" "}
                        <a href={`https://www.google.com/maps?q=${a.last_location.latitude},${a.last_location.longitude}`} target="_blank" rel="noreferrer"
                          style={{ fontFamily: "Figtree, sans-serif", fontSize: 11, color: "#0b5686" }} data-testid={`popup-coords-link-${a.id}`}>
                          Open in Maps
                        </a>
                      </p>
                      <p style={{ margin: "2px 0", fontSize: 11, color: "#64748b" }}>
                        Last seen {timeOf(a.last_location.logged_at)}{a.last_location.accuracy != null ? ` • ±${Math.round(a.last_location.accuracy)} m` : ""}
                      </p>
                    </div>
                  </Popup>
                </Marker>
              ))}
              {trail.length > 1 && (
                <Polyline positions={trail.map((t) => [t.latitude, t.longitude])}
                  pathOptions={{ color: "#b45309", weight: 3, opacity: playbackActive ? 0.25 : 0.85, dashArray: "6 8" }} />
              )}
              {playbackActive && playedTrail.length > 1 && (
                <Polyline positions={playedTrail.map((t) => [t.latitude, t.longitude])}
                  pathOptions={{ color: "#047857", weight: 4, opacity: 0.95 }} />
              )}
              {!playbackActive && trail.map((t, i) => (
                <CircleMarker key={t.id || i} center={[t.latitude, t.longitude]} radius={4}
                  pathOptions={{ color: "#b45309", fillColor: "#fff", fillOpacity: 1, weight: 2 }}>
                  <Popup><span style={{ fontSize: 12 }}>{timeOf(t.logged_at)}</span></Popup>
                </CircleMarker>
              ))}
              {currentPoint && (
                <CircleMarker center={[currentPoint.latitude, currentPoint.longitude]} radius={9}
                  pathOptions={{ color: "#047857", fillColor: "#10b981", fillOpacity: 0.9, weight: 3 }}>
                  <Popup>
                    <span style={{ fontSize: 12, fontWeight: 600 }}>{staffLabelText(selected?.name, selected?.employee_id ?? resolveStaff(avatars, selected?.id, selected?.name)?.employee_id)}</span><br />
                    <span style={{ fontSize: 11 }}>at {timeOf(currentPoint.logged_at)}</span>
                  </Popup>
                </CircleMarker>
              )}
            </MapContainer>
          </div>
        </Card>
      </div>
    </div>
  );
}
